<template>
  <DialogElement
    id="componentSettingsTTS"
    v-show-fade-content
    :aria-label="t('tts.settings.title')"
    :modal="!attributesStore.isMobile"
    :closed-by="'any'"
    class="flex settings-tts w-full absolute h-auto left-auto top-auto right-0"
    @close="close()"
  >
    <main
      class="settings-dialog w-full pb-10 overflow-y-auto overscroll-contain show-content-fade keep-dialog"
    >
      <button
        :aria-label="t('reader.close') + ' ' + t('tts.settings.title')"
        class="button close-sidebar fixed flex justify-center items-center cursor-pointer rounded-full"
        @click="close()"
      >
        <span class="material-icons">close</span>
      </button>
      <h2 class="fixed w-full text-center pb-3 pt-3 pl-12 pr-8 text-xl">
        {{ t("tts.settings.title") }}
      </h2>
      <form class="pt-16 px-6" :aria-label="t('tts.settings.controls')">
        <div class="tts-highlight mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold flex-auto">
            {{ t("tts.settings.highlight") }}
          </span>
          <div
            class="flex items-center mt-2"
            role="group"
            :aria-label="t('tts.settings.highlightSelector')"
          >
            <button
              v-for="(hexColor, colorName) in highlightColorMap"
              :key="colorName"
              class="rounded-button"
              :class="`rounded-button--${colorName}`"
              :aria-label="
                t('tts.settings.highlightButton') +
                ' ' +
                t(`tts.settings.highlightColor.${colorName}`)
              "
              @click.prevent="handleChangeColor(hexColor)"
            ></button>

            <input
              :id="settingName.ttsHighlight"
              v-model="settingStore.perContent.ttsHighlight"
              :name="settingName.ttsHighlight"
              :aria-label="t('tts.settings.customColorPalette')"
              type="color"
            />
          </div>
        </div>
        <div v-if="wpsClient.media.isFXL || wpsClient.media.isPDF" class="mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold">
            {{ t("settings.autoTurn") }}
          </span>
          <CustomToggle
            v-model="settingStore.perContent.ttsAutoTurn"
            name="autoTurn"
            :label="t('settings.autoTurn')"
          />
        </div>
        <div class="mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold">
            {{ t("tts.settings.voiceSpeed") }}
          </span>
          <RangeSelector
            :range-value="settingStore.perContent.ttsVoiceSpeed"
            :min-value="0.5"
            :max-value="2"
            :step="0.1"
            @range-selector-changed="
              settingStore.perContent.ttsVoiceSpeed = $event
            "
          />
        </div>

        <div class="mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold">
            {{ t("tts.settings.voicePitch") }}
          </span>
          <RangeSelector
            :range-value="settingStore.perContent.ttsVoicePitch"
            :min-value="0.5"
            :max-value="2"
            :step="0.1"
            @range-selector-changed="
              settingStore.perContent.ttsVoicePitch = $event
            "
          />
        </div>

        <div class="mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold">
            {{ t("tts.settings.voice") }}
          </span>
          <CategoriesList
            v-model="settingStore.perContent.ttsVoice"
            list-name="Voice"
            :items-list="voicesByCategory"
          />
        </div>

        <div class="mt-6">
          <span class="mx-0 my-auto w-28 pr-3 font-bold flex">
            {{ t("tts.settings.language") }}
          </span>
          <CustomSelectList
            v-model="settingStore.perContent.ttsLang"
            list-name="Language"
            :items-list="settingTTSLanguage"
            :list-label="t('tts.settings.language')"
            :label-translation="true"
          />
        </div>
      </form>
    </main>
  </DialogElement>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { UserSetting } from "@/helpers/settings";
import {
  settingName,
  countryLabels,
  defaultSettings as settings,
  highlightColorMap,
} from "@/helpers/constants";
import { settingValue, categorySetting } from "@/helpers/types";
import DialogElement from "@/components/shared/DialogElement.vue";
import CustomSelectList from "@/components/settings/CustomSelectList.vue";
import CustomToggle from "@/components/settings/CustomToggle.vue";
import RangeSelector from "@/components/settings/RangeSelector.vue";
import CategoriesList from "@/components/settings/CategoriesList.vue";

import { useTTSStore } from "@/stores/ttsState";
import { useAttributesStore } from "@/stores/attributes";
import { useSettingsStore } from "@/stores/settings";

import { useWpsClient } from "@/wps/client";

import { useUILayers } from "../UILayers";

const { t } = useI18n();

const wpsClient = useWpsClient();

const { close } = useUILayers();

const attributesStore = useAttributesStore();
const settingStore = useSettingsStore();
const ttsStore = useTTSStore();

const settingTTSLanguage = ref<Array<settingValue>>([]);

onMounted(async () => {
  settingTTSLanguage.value = settings
    .find((setting: UserSetting) => setting.name === settingName.ttsLang)
    ?.options.filter((option: settingValue) =>
      languageInList(option.value as string)
    );
});

const vShowFadeContent = {
  mounted: (el) => {
    const nav = el.getElementsByClassName("show-content-fade")[0];
    setTimeout(() => {
      nav?.classList.add("show-nav");
    }, 300);
  },
  onBeforeMount: (el) => {
    const nav = el.getElementsByClassName("show-content-fade")[0];
    nav?.classList.remove("show-nav");
  },
};

function handleChangeColor(color: `#${string}`) {
  settingStore.perContent.ttsHighlight = color;
}

const voicesByCategory = computed<categorySetting[]>(() => {
  const byCategory: Record<string, settingValue[]> = {};
  const recommendedVoices: settingValue[] = [];

  ttsStore.voicesForLanguage.forEach((voice) => {
    const categoryLabel = countryLabels[voice.lang] || voice.lang;
    if (!byCategory[categoryLabel]) {
      byCategory[categoryLabel] = [];
    }
    const recommended = ttsStore.recommendedVoices.find(
      (v) => v.name === voice.name
    );
    const options: settingValue = {
      label: recommended?.label || voice.name,
      value: voice.name,
    };

    // We need to filter out the repeated voices in recommended category (also cheking altNames)
    if (
      recommended &&
      !recommendedVoices.some((v) => v.label === recommended.label) &&
      !recommendedVoices.some((v) =>
        recommended?.altNames?.some(
          (altName) => altName === v.value || altName === v.label
        )
      )
    ) {
      recommendedVoices.push(options);
    }
    byCategory[categoryLabel].push(options);
  });

  // Sort voices in recommended category based on recommended list order
  recommendedVoices.sort((a, b) => {
    return (
      ttsStore.recommendedVoices.findIndex(
        (v) => v.label === a.label || v.name === a.label
      ) -
      ttsStore.recommendedVoices.findIndex(
        (v) => v.label === b.label || v.name === b.label
      )
    );
  });

  const categories = Object.entries(byCategory).map(([category, options]) => {
    //filter each category to remove duplicated voices
    //Reverse the options array to get the enhanced voices first
    options.reverse();
    options = options.filter((a, idx) => {
      return idx === options.findIndex((b) => a.label === b.label);
    });
    //Reverse the array again to keep the original order
    options.reverse();
    return {
      label: category,
      options: options,
    };
  });

  if (recommendedVoices.length) {
    categories.unshift({
      label: t("tts.settings.recommendedVoices"),
      options: recommendedVoices,
    });
  }

  return categories;
});

function languageInList(language: string): boolean {
  return ttsStore.availableLanguages.has(language);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.settings-tts {
  z-index: 5;
  height: 60vh;
  bottom: 0;
  padding: 0;
  max-height: var(--safe-height);
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  border-bottom: $spacingM var(--DM__theme-fill-color) solid;
  border-top: $spacingM var(--DM__theme-fill-color) solid;
  border-top-right-radius: $spacingM;
  border-top-left-radius: $spacingM;
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 3px solid rgba(158, 158, 158, 0.2);

  @media (min-width: $screenS) {
    max-height: calc(var(--safe-height) - 3 * $barHeight);
    border-radius: 0;
    right: $spacingM;
    max-width: $screenXS;
    bottom: $barHeight + $spacingM;
  }

  .settings-dialog {
    h2 {
      border-bottom: 1px solid var(--DM__theme-hover-color);
      background-color: var(--DM__theme-fill-color);
      z-index: 1;
      max-width: calc(100% - 6px);
      border-top-right-radius: $spacingM;
      border-top-left-radius: $spacingM;
      @media (min-width: $screenS) {
        max-width: calc($screenXS - 64px);
        border-radius: 0;
      }
    }

    @include scrollBar();
  }

  .tts-highlight {
    max-width: 15.5rem;
    input[type="color"] {
      height: 2rem;
      max-width: 2rem;
      background: transparent;
      border: 3px solid var(--DM__theme-fill-color);
    }
  }

  .close-sidebar {
    z-index: 2;
    top: auto;
    left: auto;
    margin: 10px 0 0 6px;
  }

  .rounded-button {
    height: 2rem;
    width: 2rem;
    border-radius: 50%;
    border: 3px solid;
    margin-right: 1rem;
    border-color: var(--DM__theme-fill-color);
    &--yellow {
      background-color: rgb(var(--DM__color-yellowRGBColor));
    }
    &--red {
      background-color: rgb(var(--DM__color-redRGBColor));
    }
    &--green {
      background-color: rgb(var(--DM__color-greenRGBColor));
    }
    &--blue {
      background-color: rgb(var(--DM__color-blueRGBColor));
    }
  }
}
</style>
